import { isNil } from 'lodash';
import { useCallback, useContext, useState } from 'react';
import { StyleSheet } from 'react-native';
import { Button } from '@shared/components/Button';
import { Text } from '@shared/components/Text';
import { View } from '@shared/components/View';
import { Pill } from '@shared/components/pill';
import { MBTextInput } from '@shared/components/textInput';
import { TitledWorkflowTemplate, getEntityTitle, isDeletedEntity } from '@shared/data/entity';
import { RelatedItem } from '@shared/data/entity/fields/RelatedEntityField';
import { ColorThemeContext, grid } from '@shared/styles';
import { API } from '@shared/types/api';
import { i18n } from '@shared/util/i18n';
import { useFieldDraft } from '../useFieldDraft';
import { MAX_CARDS_TO_SHOW } from './constants';
import classNames from './RelatedQuantityList.module.css';

interface Props {
  component: API.RelatedCardComponent;
  items: RelatedItem[];
  entities: API.WorkflowEntity[];
  openPicker: VoidFunction;
  updateItemQuantity: (id: string, quantity: number) => void;
  onItemPress?: (entity?: API.WorkflowEntity) => void;
  onItemRemove?: (entity?: API.WorkflowEntity) => void;
  openingEntity: API.WorkflowEntity | null;
  loading: boolean;
  workflowTemplate?: TitledWorkflowTemplate;
}

export function RelatedQuantityList({
  component,
  items,
  entities,
  openPicker,
  updateItemQuantity,
  onItemPress,
  onItemRemove,
  openingEntity,
  loading,
  workflowTemplate,
}: Props) {
  const isReadOnly = component.readonly ?? false;
  const [showAll, setShowAll] = useState(false);
  const count = entities.length;
  const renderShowMore = count > MAX_CARDS_TO_SHOW;
  const remainingCount = count - MAX_CARDS_TO_SHOW;
  const itemsToShow = showAll ? items : items.slice(0, MAX_CARDS_TO_SHOW);
  const defaultEntityName = i18n('workflowTemplate.defaultRelatedCard', {
    workflowName: component.label,
  });

  const showLessLabel = i18n('card.list.showLess');
  const showMoreCountLabel = i18n('card.blocks.cardsInput.overflow', { count: remainingCount });
  const showMoreLabel = showAll ? showLessLabel : showMoreCountLabel;

  return (
    <View paddingTop={1.25} paddingBottom={0.5}>
      <ColumnHeaders />
      <View>
        {itemsToShow.map(item => (
          <RelatedQuantityItem
            key={item.id}
            isReadOnly={isReadOnly}
            onItemPress={onItemPress}
            onItemRemove={onItemRemove}
            openingEntity={openingEntity}
            item={item}
            entities={entities}
            workflowTemplate={workflowTemplate}
            defaultEntityName={defaultEntityName}
            openPicker={openPicker}
            updateItemQuantity={updateItemQuantity}
            loading={loading}
          />
        ))}
      </View>
      {renderShowMore && (
        <View row paddingVertical={0.5}>
          <Button
            variant="tertiary"
            size="small"
            label={showMoreLabel}
            onClick={() => setShowAll(prev => !prev)}
          />
        </View>
      )}
    </View>
  );
}

function ColumnHeaders() {
  const colors = useContext(ColorThemeContext);
  return (
    <View row>
      <View style={{ width: grid(5) }}>
        <Text size="overline" align="center" color={colors.input.label}>
          {i18n('card.blocks.cardsInput.quantity.header.quantity')}
        </Text>
      </View>
      <View paddingLeft={2}>
        <Text size="overline" color={colors.input.label}>
          {i18n('card.blocks.cardsInput.quantity.header.name')}
        </Text>
      </View>
    </View>
  );
}

interface RelatedQuantityItemProps extends Omit<Props, 'component' | 'items' | 'onShowMore'> {
  item: RelatedItem;
  defaultEntityName: string;
  isReadOnly: boolean;
}

function RelatedQuantityItem({
  item,
  defaultEntityName,
  entities,
  updateItemQuantity,
  isReadOnly,
  onItemPress,
  onItemRemove,
  openingEntity,
  loading,
  workflowTemplate,
}: RelatedQuantityItemProps) {
  const quantity = item.quantity ?? 1;
  const [draft, commitDraft] = useFieldDraft<string, number>(quantity);
  const value = draft ?? String(quantity);
  const colors = useContext(ColorThemeContext);
  const entity = entities.find(e => e.id === item.id);
  const entityName = getEntityTitle(entity, workflowTemplate);
  const isMissingName = isNil(entityName);
  const isDeleted = isDeletedEntity(entity);

  const updateValue = useCallback(
    (updatedValue: string) => {
      if (!updatedValue.match(/^\d*$/)) {
        return;
      }
      const number = parseValue(updatedValue);
      // an empty input isn't a quantity yet — hold it in the draft until it parses
      commitDraft(updatedValue, isNaN(number) ? quantity : number);
      if (!isNaN(number)) {
        updateItemQuantity(item.id, number);
      }
    },
    [commitDraft, updateItemQuantity, item.id, quantity],
  );

  const onBlur = useCallback(() => {
    const number = parseValue(value);
    const updatedQuantity = isNaN(number) ? 1 : number;
    commitDraft(String(updatedQuantity), updatedQuantity);
    updateItemQuantity(item.id, updatedQuantity);
  }, [value, commitDraft, updateItemQuantity, item.id]);

  return (
    <View className={classNames.cardRow}>
      <MBTextInput
        value={value}
        onChangeText={updateValue}
        onBlur={onBlur}
        size="normal"
        keyboardType="number-pad"
        noPadding
        style={[styles.input, { borderColor: colors.input.borderInactive }]}
        editable={!isReadOnly}
      />
      <Pill
        sections={[entityName ?? defaultEntityName]}
        italic={isDeleted || isMissingName}
        maxWidth={250}
        onPress={isDeleted ? undefined : () => onItemPress?.(entity)}
        onRemove={isNil(onItemRemove) ? undefined : () => onItemRemove(entity)}
        loading={loading || (!isNil(entity?.id) && entity?.id === openingEntity?.id)}
      />
    </View>
  );
}

function parseValue(value: string): number {
  const cleanedValue = value.replace(/\D/g, '');
  return parseInt(cleanedValue, 10);
}

const styles = StyleSheet.create({
  input: {
    height: 'auto',
    flexShrink: 0,
    flexGrow: 0,
    paddingVertical: grid(0.25),
    width: grid(5),
    textAlign: 'center',
    borderRadius: grid(0.5),
    marginVertical: 4,
    marginRight: grid(0.5),
  },
});
